import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  Box, 
  Container, 
  Typography, 
  CircularProgress, 
  Alert,
  AppBar,
  Toolbar,
  Button,
  Link
} from '@mui/material';
import PuckRenderer from '../../components/MicroSite/PuckRenderer';
import AnalyticsTracker from '../../components/Analytics/AnalyticsTracker';

const PublicPage = () => {
  const location = useLocation();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [site, setSite] = useState(null);
  const [page, setPage] = useState(null);
  // Add state for tracking analytics
  const [analyticsInitialized, setAnalyticsInitialized] = useState(false);
  const [analyticsDebug, setAnalyticsDebug] = useState({
    pageId: null,
    microSiteId: null,
    accountId: null
  });

  useEffect(() => {
    const fetchPageData = async () => {
      setLoading(true);
      setError(null);
      
      try {
        // Extract subdomain from hostname
        const hostname = window.location.hostname;
        const hostParts = hostname.split('.');
        let subdomain = null;
        
        if (hostParts.length > 2 && hostParts[0] !== 'www') {
          subdomain = hostParts[0];
        } else {
          // For local development or when not using a subdomain
          // Try to extract from the path
          const pathParts = location.pathname.split('/');
          if (pathParts.length > 1 && pathParts[1]) {
            subdomain = pathParts[1];
          }
        }
        
        if (!subdomain) {
          throw new Error('Could not determine subdomain');
        }
        
        // Determine the slug from the path
        let slug = 'home'; // Default to home page
        const path = location.pathname;
        
        if (path === '/' || path === `/${subdomain}`) {
          // Root path or just subdomain, use default slug
          slug = 'home';
        } else if (path.startsWith(`/${subdomain}/`)) {
          // Path includes subdomain, extract the rest
          slug = path.substring(subdomain.length + 2);
        } else if (path.startsWith('/')) {
          // Path doesn't include subdomain
          slug = path.substring(1);
        }
        
        // Remove trailing slash if present
        if (slug.endsWith('/')) {
          slug = slug.slice(0, -1);
        }
        
        console.log('Fetching page data:', { subdomain, slug, hostname, path });
        
        // First fetch the site data
        // const siteResponse = await fetch(`/api/public/sites/${subdomain}`, {
        //   headers: {
        //     'Accept': 'application/json',
        //     'Cache-Control': 'no-cache, no-store, must-revalidate',
        //     'Pragma': 'no-cache',
        //     'Expires': '0'
        //   }
        // });
        
        // if (!siteResponse.ok) {
        //   throw new Error(`Failed to fetch site: ${siteResponse.status} ${siteResponse.statusText}`);
        // }
        
        // const siteData = await siteResponse.json();
        // console.log('Fetched site data:', siteData);
        // setSite(siteData);
        
        // Then fetch the page data
        const pageResponse = await fetch(`/api/public/sites/${subdomain}/pages/${slug}`, {
          headers: {
            'Accept': 'application/json',
            'Cache-Control': 'no-cache, no-store, must-revalidate',
            'Pragma': 'no-cache',
            'Expires': '0'
          }
        });
        
        if (!pageResponse.ok) {
          throw new Error(`Failed to fetch page: ${pageResponse.status} ${pageResponse.statusText}`);
        }
        
        // Get the response text first to check if it's HTML or JSON
        const responseText = await pageResponse.text();
        
        // Check if the response is HTML (starts with <!DOCTYPE or <html)
        if (responseText.trim().toLowerCase().startsWith('<!doctype') || 
            responseText.trim().toLowerCase().startsWith('<html')) {
          console.error('Received HTML response instead of JSON');
          console.error('HTML response (first 100 chars):', responseText.substring(0, 100));
          throw new Error('Server returned HTML instead of JSON. The API endpoint may not be configured correctly.');
        }
        
        // Try to parse the response as JSON
        let pageData;
        try {
          pageData = JSON.parse(responseText);
          console.log('Fetched page data:', pageData);
        } catch (e) {
          console.error('Error parsing JSON:', e);
          console.error('Response text (first 100 chars):', responseText.substring(0, 100));
          throw new Error('Invalid JSON response from server');
        }
        
        // Check the structure of the page data
        if (pageData.page) {
          // If the API returns { site, page } structure
          setPage(pageData.page);
          // Update site data if it's included in the response
          if (pageData.site) {
            setSite(pageData.site);
          }
        } else {
          // If the API returns just the page data directly
          setPage(pageData);
        }
      } catch (err) {
        console.error('Error fetching page:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    
    fetchPageData();
  }, [location.pathname]);

  // Set page title and meta description
  useEffect(() => {
    if (page && site) {
      document.title = `${page.title} | ${site?.name || 'Website'}`;
      
      // Update meta description if available
      const metaDescription = document.querySelector('meta[name="description"]');
      if (metaDescription && page.metaDescription) {
        metaDescription.setAttribute('content', page.metaDescription);
      }
      
      // Make sure accountId is available
      const siteAccountId = site.accountId;
      
      if (!siteAccountId) {
        console.warn('Missing accountId in site data. Analytics will attempt to retrieve it from the server.');
      }
      
      // Store analytics data for debugging
      setAnalyticsDebug({
        pageId: page.id,
        microSiteId: site.id,
        accountId: siteAccountId
      });
      
      // Initialize analytics after page data is loaded
      setAnalyticsInitialized(true);
    }
  }, [page, site]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Typography variant="body1">
          Sorry, we couldn't load this page. Please try again later.
        </Typography>
      </Container>
    );
  }

  if (!page || !site) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="warning" sx={{ mb: 2 }}>
          Page not found
        </Alert>
        <Typography variant="body1">
          The page you're looking for doesn't exist or has been moved.
        </Typography>
      </Container>
    );
  }

  // Apply site theme if available
  const theme = site.theme || {};
  const primaryColor = theme.primaryColor || '#1976d2';
  const secondaryColor = theme.secondaryColor || '#f50057';
  const backgroundColor = theme.backgroundColor || '#ffffff';
  const textColor = theme.textColor || '#333333';

  // Ensure page content is properly formatted for the PuckRenderer
  let pageContent = page.content;
  
  // If content is a string, try to parse it as JSON
  if (typeof pageContent === 'string') {
    try {
      pageContent = JSON.parse(pageContent);
      console.log('Successfully parsed page content as JSON');
    } catch (e) {
      console.warn('Page content is not valid JSON, treating as HTML/text');
      
      // If it's not valid JSON, convert it to a Puck-compatible format
      pageContent = {
        content: [
          {
            type: 'TextBlock',
            props: {
              heading: page.title,
              content: pageContent,
              backgroundColor: '#ffffff',
              textColor: '#333333',
              padding: 'medium',
              align: 'left',
              maxWidth: 'large'
            }
          }
        ],
        root: {
          props: {
            style: {
              backgroundColor: "#ffffff"
            }
          }
        }
      };
    }
  } else if (!pageContent || (typeof pageContent === 'object' && Object.keys(pageContent).length === 0)) {
    // If content is empty or an empty object, create a default structure
    pageContent = {
      content: [
        {
          type: 'TextBlock',
          props: {
            heading: page.title,
            content: '<p>This page has no content yet.</p>',
            backgroundColor: '#ffffff',
            textColor: '#333333',
            padding: 'medium',
            align: 'left',
            maxWidth: 'large'
          }
        }
      ],
      root: {
        props: {
          style: {
            backgroundColor: "#ffffff"
          }
        }
      }
    };
  }

  return (
    <Box sx={{ 
      minHeight: '100vh', 
      backgroundColor: backgroundColor,
      color: textColor
    }}>
      {/* Site Header */}
      <AppBar 
        position="static" 
        sx={{ 
          backgroundColor: primaryColor,
          color: '#ffffff'
        }}
      >
        <Toolbar>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {site.name}
          </Typography>
          
          {/* Navigation links - these would come from site.settings */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button 
              id="nav-home-button" 
              color="inherit" 
              component={Link} 
              href="/"
            >
              Home
            </Button>
            {site.settings?.navigation?.map((item, index) => (
              <Button 
                key={index}
                id={`nav-${item.slug}-button`}
                color="inherit" 
                component={Link} 
                href={`/${item.slug}`}
              >
                {item.label}
              </Button>
            ))}
          </Box>
        </Toolbar>
      </AppBar>
      
      {/* Page Content */}
      <Box component="main" id="page-content">
        {/* Render the Puck content */}
        <PuckRenderer 
          content={pageContent} 
          accountId={site.accountId}
          microSiteId={site.id}
          pageId={page.id}
        />
        
        {/* Add the analytics tracker - only render when data is available */}
        {analyticsInitialized && page && site && (
          <AnalyticsTracker
            pageId={page.id}
            microSiteId={site.id}
            accountId={site.accountId}
            trackInteractions={true}
          />
        )}
      </Box>
      
      {/* Site Footer */}
      <Box 
        component="footer" 
        id="site-footer"
        sx={{ 
          py: 3, 
          px: 2, 
          mt: 'auto', 
          backgroundColor: theme.footerColor || primaryColor,
          color: '#ffffff'
        }}
      >
        <Container maxWidth="lg">
          <Typography variant="body2" align="center">
            {site.settings?.footerText || `© ${new Date().getFullYear()} ${site.name}`}
          </Typography>
        </Container>
      </Box>
    </Box>
  );
};

export default PublicPage; 